import React, { useEffect, useState } from 'react';
import { client, Collection, common } from 'plato-js-client';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from 'reactstrap';
import Check from '../fields/Check';
import Icon from '../components/Icon';
import SelectList from '../fields/SelectList';
import Text from '../fields/Text';
import { successToast } from '../components/Toasties';
import moment from 'moment';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { globalChecklistsQueryOptions } from '../checklists/checklistQueries';

export default function JobChecklistBlock({job}) {

  const checklists = useQuery(globalChecklistsQueryOptions());

  const [addJobChecklistModal, setAddJobChecklistModal] = useState(false);
  const [jobChecklists, setJobChecklists] = useState();
  const [checklist, setChecklist] = useState();
  const [ showChecklistId, setShowChecklistId ] = useState(null);

  const getJobChecklists = async () => {
    var collection = new Collection({
      path: 'pmschecklist',
      object: common.WorkOrderPmsChecklist,
      parent: job,
    });

    await collection.fetch();
    setJobChecklists(collection.collection);
  }

  useEffect(() => {
    if (job) {
      getJobChecklists();
    }
  }, []);

  const toggleAddJobChecklistModal = () => {
    setAddJobChecklistModal(!addJobChecklistModal);
  }

  const addChecklist = () => {
    let wocl = new common.WorkOrderPmsChecklist();
    wocl.parent = job;
    wocl.pmschecklist = checklist;
    wocl.json = "{}";
    wocl.updateddatetime = moment();

    wocl.create().then(() => {
      getJobChecklists();
      toggleAddJobChecklistModal();
      successToast('new checklist added!');
    });
  }

  if (checklists.isLoading) {
    return <>Loading...</>;
  }

  return (
    <React.Fragment>
      <Row className="mb-2">
        <Col>
          { jobChecklists && jobChecklists.length > 0 &&
              <Table
                  size="sm"
                  bordered
                  striped
              >
                <thead>
                <tr>
                  <th>Title</th>
                  <th></th>
                  <th>Last Update</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                { jobChecklists.map(cl =>
                    <JobChecklistBlockRow
                        key={ cl.id }
                        job={ job }
                        checklist={ cl }
                        onClick={ () => setShowChecklistId(cl.id) }
                        getJobChecklists={ getJobChecklists }
                    />,
                ) }
                </tbody>
              </Table>
          }
          <Button
              onClick={ toggleAddJobChecklistModal }
              disabled={ !job }
          >add checklist</Button>{ ' ' }
        </Col>
      </Row>
      {addJobChecklistModal &&
      <Modal isOpen toggle={toggleAddJobChecklistModal} size="lg">
        <ModalHeader toggle={toggleAddJobChecklistModal}>
          add checklist
        </ModalHeader>
        <ModalBody>
          <Input 
            type="select" 
            onChange={e => {
              const cl = checklists.data.find(cl => cl.id === parseInt(e.target.value, 10));
              if (cl) {
                setChecklist(cl);
              } else {
                setChecklist(undefined);
              }
            }}
          >
            <option key={0} value={0}>select a checklist</option>
            {checklists.data
              .filter((cl) => cl.inactive === false)
              .map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.name}
                </option>
              ))
            }
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" disabled={!checklist} onClick={addChecklist}>add</Button>{' '}
          <Button color="secondary" onClick={toggleAddJobChecklistModal}>cancel</Button>
        </ModalFooter>
      </Modal>
      }
      <JobChecklistModal
          job={ job }
          checklistId={ showChecklistId }
          toggle={ () => setShowChecklistId(null) }
      />
    </React.Fragment>
  );
}

function JobChecklistBlockRow({checklist, job, getJobChecklists, onClick }) {

  const deleteChecklist = () => {
    if (window.confirm('Are you sure you want to remove this checklist?\nThis action cannot be undone.')) {
      let wopc = new common.WorkOrderPmsChecklist(checklist.id);
      wopc.parent = job;

      wopc.delete().then(() => {
        getJobChecklists();
      });
    }
  };

  return (
      <tr>
        <td>
          { checklist.flaggedForReview && <Badge
              color={ 'warning' }
              className={ 'mr-2' }
          >Needs Review</Badge> }
          { checklist.pmschecklist.name }
        </td>
        <td>
          <Button size={ 'sm' } onClick={ onClick }>View</Button>
        </td>
        <td>
          { moment(checklist.updateddatetime).format('DD/MM/YY HH:mm') }
        </td>
        <td>
          <Icon
              icon="trash"
              onClick={ deleteChecklist }
          />
        </td>
      </tr>
  );
}

function loadAnswers(groups) {
  const answersByQuestionId = {};

  for (const gi in groups) {
    const g = groups[gi];
    for (const qi in g.questions) {
      const q = g.questions[qi];
      switch (q.type) {
        case 'check':
          answersByQuestionId[q.id] = q.checked ?? false;
          break;
        case 'checklist':
          answersByQuestionId[q.id] = q.answers ?? [];
          break;
        default:
          answersByQuestionId[q.id] = q.answer ?? '';
      }
    }
  }

  return answersByQuestionId;
}

function JobChecklistModal({
  checklistId,
  job,
  toggle,
}) {
  const [ answers, setAnswers ] = useState({});

  const queryKey = [ 'checklist.view', { checklistId } ];
  const queryClient = useQueryClient();
  const checklistQuery = useQuery({
    enabled: Boolean(checklistId),
    queryKey: queryKey,
    queryFn: async () => {
      try {
        const wopc = new common.WorkOrderPmsChecklist(checklistId);
        wopc.parent = job;
        const checklist = await wopc.get();

        const checklistWithResponse = JSON.parse(checklist.json);
        if (checklistWithResponse.groups) {
          return {
            ...checklistWithResponse,
            flags: checklist.flags.collection,
            title: checklist.pmschecklist.name,
            answersByQuestionId: loadAnswers(checklistWithResponse.groups),
            flaggedForReview: checklist.flaggedForReview,
            flaggedReason: checklist.flaggedReason,
          };
        }

        // checklist hasn't been filled in yet
        const checklistWithoutResponse = JSON.parse(checklist.pmschecklist.json);
        if (checklistWithoutResponse.groups) {
          return {
            ...checklistWithoutResponse,
            flags: [],
            title: checklist.pmschecklist.name,
            answersByQuestionId: {},
            flaggedForReview: checklist.flaggedForReview,
            flaggedReason: checklist.flaggedReason,
          };
        }
      } catch (e) {
        console.error(e);
      }

      return {
        groups: [],
        flags: [],
        title: '',
        answersByQuestionId: {},
        flaggedForReview: false,
        flaggedReason: '',
      };
    },
    placeholderData: {
      groups: [],
      flags: [],
      answersByQuestionId: {},
      title: '',
      flaggedForReview: false,
      flaggedReason: '',
    }
  });

  const reviewMutation = useMutation({
    mutationKey: ['checklist.clear-flag', { checklistId }],
    mutationFn: async () => {
      let wopc = new common.WorkOrderPmsChecklist(checklistId);
      wopc.parent = job;
      return client.getInstance().put({
        path: wopc.getUpdatePath(),
        entity: {
          clearFlag: true,
        },
      });
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKey, (data) => ({
        ...data,
        flaggedForReview: false,
      }));

      // NOTE: Currently unable to clear the flag on the top-level table. The option is to use tell PMS to refetch
      // the entire list of checklists.
    },
  });

  const saveMutation = useMutation({
    mutationKey: ['checklist.save', { checklistId }],
    mutationFn: async () => {
      const wopc = new common.WorkOrderPmsChecklist(checklistId);
      wopc.parent = job;

      const changes = {
        '$version': 2,
        groups: checklistQuery.data.groups.map(group => {
          const questions = group.questions.map(question => {
            const answer = {};
            switch (question.type) {
              case 'check':
                answer.checked = answers[question.id];
                break;
              case 'checklist':
                answer.answers = answers[question.id];
                break;
              default:
                answer.answer = answers[question.id];
            }

            return {
              ...question,
              ...answer,
            };
          });

          return {
            ...group,
            questions,
          };
        })
      };

      await wopc.update({
        json: JSON.stringify(changes),
      });

      return {
        // The changed bit of "state" to store in the query cache.
        // LESSON LEARNED: Don't let the queryFn return value ever define the shape of the component state,
        // or suffer the consequences of having both query and mutations, which ought to treated as
        // reusable & component-agnostic, have to comply with that contract/shape.
        answersByQuestionId: loadAnswers(changes.groups),
      };
    },
    onSuccess: (changes) => {
      queryClient.setQueryData(queryKey, (data) => ({
        ...data,
        ...changes,
      }));

      toggle();
    }
  })

  useEffect(() => {
    // Using useEffect for this is a hack, but it saves us from moving lots of legacy code around.
    if (checklistQuery.isSuccess) {
      setAnswers(checklistQuery.data.answersByQuestionId);
    }
  }, [ checklistQuery.data, checklistQuery.isSuccess ]);

  if (!checklistId) {
    return null;
  }

  const handleChange = (questionId, value) => {
    setAnswers(currentAnswers => ({
      ...currentAnswers,
      [questionId]: value,
    }));
  };

  const previewChecklist = (answersonly) => {
    const cl = new common.WorkOrderPmsChecklist(checklistId);
    cl.answersonly = answersonly;
    cl.parent = job;

    cl.render().then((res) => {
      const link = document.createElement('a');
      link.setAttribute('href', res.entity.url);
      link.setAttribute('download', 'checklist' + checklistId + '.pdf');
      link.setAttribute('target', '_blank');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (<Modal
          isOpen
          key={ `view.checklist-${ checklistId }` }
          toggle={ toggle }
          size="lg"
      >
        <ModalHeader toggle={ toggle }>
          <span className="font-weight-bold">{ checklistQuery.data.title }</span>
        </ModalHeader>
        { checklistQuery.isLoading && <ModalBody>Loading...</ModalBody> }
        { checklistQuery.isSuccess && <ModalBody>
          <ButtonGroup className="mb-2">
            <Button
                onClick={ () => previewChecklist(true) }
            >download PDF</Button>
            <Button
                onClick={ () => previewChecklist(false) }
            >download full PDF</Button>
          </ButtonGroup>
          { checklistQuery.data.flaggedForReview && <Alert color="warning">
            <h6 className={ 'font-weight-bold' }>Review Required</h6>
            <p>Cleaner message:</p>
            <hr />
            { checklistQuery.data.flaggedReason }
          </Alert> }
          { checklistQuery.data.groups.map((group, gi) => <div key={ `group-${ gi }-${ group.id }` }>
            <div>
              <h5>{ group.name }</h5>
            </div>
            { group.questions.map((question, qi) => {
              switch (question.type) {
                case 'multi':
                  return (<JobChecklistMulti
                      key={ `question-${ qi }-${ question.id }` }
                      question={ question }
                      value={ answers[question.id] }
                      handleChange={ value => handleChange(question.id, value) }
                  />);
                case 'text':
                  return (<JobChecklistText
                      key={ `question-${ qi }-${ question.id }` }
                      question={ question }
                      value={ answers[question.id] }
                      handleChange={ value => handleChange(question.id, value) }
                  />);
                case 'checklist':
                  return (<JobChecklistChecklist
                      key={ `question-${ qi }-${ question.id }` }
                      question={ question }
                      value={ answers[question.id] ?? [] }
                      handleChange={ value => handleChange(question.id, value) }
                  />);
                case 'check':
                  return (<JobChecklistCheck
                      key={ `question-${ qi }-${ question.id }` }
                      question={ question }
                      value={ answers[question.id] }
                      handleChange={ value => handleChange(question.id, value) }
                  />);
              }
            }) }
            <hr />
          </div>) }
          <FlagHistory flags={ checklistQuery.data.flags }/>
        </ModalBody> }
        <ModalFooter>
          { checklistQuery.data.flaggedForReview &&
              <Button
                  color="warning"
                  onClick={ reviewMutation.mutate }
                  disabled={ reviewMutation.isLoading || reviewMutation.isPending }
              >Mark as Reviewed</Button> }
          <Button
              color="primary"
              disabled={ checklistQuery.isLoading || checklistQuery.isError }
              onClick={ saveMutation.mutate }
          >save changes</Button>{ ' ' }
          <Button
              color="secondary"
              onClick={ toggle }
          >cancel</Button>
        </ModalFooter>
      </Modal>
  );
}

const dateFormat = 'DD/MM/YY HH:mm';

function FlagHistory({ flags }) {
  if (!flags) {
    return null;
  }

  const cleared = flags.filter(flag => flag.cleared);
  if (cleared.length < 1) {
    return null;
  }

  return (
      <>
        <h5>Review History</h5>
        <Table
            striped
            size={ 'sm' }
        >
          <thead>
          <tr>
            <th>Reason</th>
            <th>Flagged On</th>
            <th>Resolved On</th>
            <th>Resolved By</th>
          </tr>
          </thead>
          <tbody>
          { cleared.map((flag, index) => (
              <tr key={ `checklist-flag-${ flag.id }-${ index }` }>
                <td>{ flag.reason }</td>
                <td>{ moment(flag.createdDateTime).format(dateFormat) }</td>
                <td>{ moment(flag.clearedDateTime).format(dateFormat) }</td>
                <td>{ flag.clearedBy.getFullName() }</td>
              </tr>
          )) }
          </tbody>
        </Table>
      </>
  );
}

function JobChecklistMulti({question, value, handleChange}) {
  return (
    <SelectList
      name={question.question}
      value={value}
      values={question.options}
      optionName="option"
      emptyValue="select an option"
      onChange={e => handleChange(e.target.value)}
    />
  );
}

function JobChecklistText({question, value, handleChange}) {
  return (
    <Text
      name={question.question}
      value={value}
      onChange={e => handleChange(e.target.value)}
    />
  );
}

function JobChecklistChecklist({question, value, handleChange}) {
  return (
    <FormGroup row>
      <Label sm={4}>{question.question}</Label>
      <Col sm={8}>
        {question.options.map((option, i) =>
        <FormGroup check key={i}>
          <Label check>
            <Input 
              type="checkbox" 
              name={question.question + option.id} 
              onChange={e => {
                let v = [...value];
                if (e.target.checked) {
                  v.push(option.id);
                } else {
                  v.splice(v.indexOf(option.id), 1);
                }

                handleChange(v);
              }}
              checked={value?.includes(option.id)}
            />{' '}
            {option.option}
          </Label>
        </FormGroup>
        )}             
      </Col>
    </FormGroup>
  );
}

function JobChecklistCheck({question, value, handleChange}) {
  return (
    <Check
      name={question.question}
      value={value}
      onChange={e => handleChange(e.target.checked)}
    />
  );
}
